/**
 * localStorage-backed table preferences for the legacy orders grid, matching
 * the old panel's own storage:
 *
 * - the page size lives under `table_offset_sizes`, a JSON object keyed by
 *   table id (`AgGridTable.tsx:108-110` seeded from it and wrote every
 *   change). The id here is `delivery` — note the old grid's own id was
 *   `delivery-main-table`, so the two apps keep separate entries in the same
 *   object rather than sharing one;
 * - column visibility is this port's own key (`legacy-orders-columns`) — the
 *   old app kept it in a redux-persist slice whose storage shape carried the
 *   whole AG Grid column state, which nothing else can read back.
 *
 * Every reader swallows storage/JSON failures and falls back to the default:
 * a corrupt preference must never take the orders screen down.
 */

const PAGE_SIZE_STORE_KEY = 'table_offset_sizes';
/** The old grid's table id — the key inside `table_offset_sizes`. */
const PAGE_SIZE_TABLE_ID = 'delivery';
export const LEGACY_DEFAULT_PAGE_SIZE = 15;

const COLUMNS_STORE_KEY = 'legacy-orders-columns';

/** The stored page size for the delivery grid, or the old default of 15. */
export function readStoredLegacyPageSize(): number {
  try {
    const raw = localStorage.getItem(PAGE_SIZE_STORE_KEY);
    if (!raw) return LEGACY_DEFAULT_PAGE_SIZE;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return LEGACY_DEFAULT_PAGE_SIZE;
    const size = (parsed as Record<string, unknown>)[PAGE_SIZE_TABLE_ID];
    return typeof size === 'number' && Number.isInteger(size) && size >= 1
      ? size
      : LEGACY_DEFAULT_PAGE_SIZE;
  } catch {
    return LEGACY_DEFAULT_PAGE_SIZE;
  }
}

/** Persist a chosen page size, preserving other tables' entries. */
export function storeLegacyPageSize(size: number): void {
  try {
    const raw = localStorage.getItem(PAGE_SIZE_STORE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    const store =
      typeof parsed === 'object' && parsed !== null
        ? (parsed as Record<string, unknown>)
        : {};
    localStorage.setItem(
      PAGE_SIZE_STORE_KEY,
      JSON.stringify({ ...store, [PAGE_SIZE_TABLE_ID]: size }),
    );
  } catch {
    // Storage may be unavailable (private mode, quota) — the size still
    // applies for the session through the URL.
  }
}

/**
 * The stored column-visibility map (TanStack shape: `id → visible`), or an
 * empty map — which TanStack reads as "everything visible", the old redux
 * slice's `hide: false` default for all eleven columns.
 */
export function readStoredColumnVisibility(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(COLUMNS_STORE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return {};
    }
    const entries = Object.entries(parsed as Record<string, unknown>).filter(
      (entry): entry is [string, boolean] => typeof entry[1] === 'boolean',
    );
    return Object.fromEntries(entries);
  } catch {
    return {};
  }
}

export function storeColumnVisibility(visibility: Record<string, boolean>): void {
  try {
    localStorage.setItem(COLUMNS_STORE_KEY, JSON.stringify(visibility));
  } catch {
    // Non-fatal — the choice just won't survive a reload.
  }
}
